<template>
  <div class="chat-dock">
    <section class="friend-panel" :class="{ expanded: showFriendPanel, 'blink-unread': shouldBlinkCollapsedHeader }">
      <header class="panel-header panel-handle" @click="toggleFriendPanel">
        <div class="panel-title">
          <h3>站内消息</h3>
          <p>{{ showFriendPanel ? '支持私信、群聊、@提醒' : '点击展开好友列表' }}</p>
        </div>
        <div class="panel-header-actions">
          <span v-if="fabUnreadCount > 0" class="fab-badge">{{ formatBadge(fabUnreadCount) }}</span>
          <button v-if="showFriendPanel && canCreateGroupChat" class="ghost-btn" @click.stop="openGroupChatDialog">+ 群聊</button>
          <span class="collapse-icon" :class="{ expanded: showFriendPanel }">⌃</span>
        </div>
      </header>

      <div class="panel-body" v-show="showFriendPanel">
        <div class="tab-row">
          <button :class="['tab-btn', { active: activeTab === 'conversations' }]" @click="activeTab = 'conversations'">消息</button>
          <button :class="['tab-btn', { active: activeTab === 'friends' }]" @click="activeTab = 'friends'">好友</button>
        </div>

        <div class="search-row">
          <input
            v-model.trim="listKeyword"
            class="search-input"
            type="text"
            :placeholder="activeTab === 'friends' ? '搜索好友' : '搜索消息'"
          />
        </div>

        <div class="list-wrap" v-if="activeTab === 'friends'">
          <div v-if="loadingFriends" class="empty-row">加载好友中...</div>
          <template v-else>
            <div
              v-for="friend in visibleFriends"
              :key="friend.id"
              class="friend-item"
              @click="openDirectConversation(friend)"
            >
              <div class="avatar-wrap">
                <img class="avatar" :src="friend.avatar || fallbackAvatar" alt="avatar" />
                <span class="online-dot" :class="{ online: !!friend.online }"></span>
              </div>
              <div class="friend-info">
                <p class="name">{{ friend.username }}</p>
                <p class="desc">点击开始私信</p>
              </div>
              <button
                class="tiny-btn"
                v-if="!friend.blocked"
                @click.stop="blockUser(friend)"
              >屏蔽</button>
              <button
                class="tiny-btn danger"
                v-else
                @click.stop="unblockUser(friend)"
              >取消</button>
            </div>
            <div v-if="!friendSearchKeyword" class="empty-row">请输入用户名或ID进行搜索</div>
            <div v-else-if="!visibleFriends.length" class="empty-row">未找到匹配用户</div>
          </template>
        </div>

        <div class="list-wrap" v-else>
          <div v-if="loadingConversations" class="empty-row">加载会话中...</div>
          <template v-else>
            <div
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="conversation-item"
              @click="openConversation(conversation)"
            >
              <div class="avatar-wrap">
                <img class="avatar" :src="getConversationAvatar(conversation)" alt="avatar" />
                <span
                  v-if="conversation.conversationType === 'direct'"
                  class="online-dot"
                  :class="{ online: isConversationOnline(conversation) }"
                ></span>
              </div>
              <div class="conversation-main">
                <p class="name">{{ getConversationTitle(conversation) }}</p>
                <p class="preview">{{ getConversationPreview(conversation) }}</p>
              </div>
              <div class="conversation-side">
                <span v-if="showFriendPanel && conversation.unreadCount > 0" class="unread-tag">{{ formatBadge(conversation.unreadCount) }}</span>
                <span v-if="conversation.mentionCount > 0" class="mention-tag">@{{ conversation.mentionCount }}</span>
              </div>
            </div>
            <div v-if="!filteredConversations.length" class="empty-row">暂无会话，点击好友发起聊天</div>
          </template>
        </div>
      </div>
    </section>

    <transition name="chat-slide">
      <section v-show="showChatWindow" class="chat-window">
        <header class="chat-header" v-if="activeConversation">
          <div class="chat-header-main">
            <h3>{{ getConversationTitle(activeConversation) }}</h3>
            <p>
              {{ getConversationTypeLabel(activeConversation) }}
              <span class="ws-state" :class="{ online: wsConnected }">{{ wsConnected ? '实时连接中' : '轮询模式' }}</span>
            </p>
          </div>
          <button class="ghost-btn" @click.stop="closeChatWindow">关闭</button>
        </header>

        <div class="chat-toolbar">
          <input
            v-model.trim="messageKeyword"
            type="text"
            class="search-input"
            placeholder="搜索当前会话消息"
            @keyup.enter="searchMessages"
          />
          <button class="tiny-btn" @click="searchMessages">搜索</button>
        </div>

        <div class="message-list" ref="messageListRef" @click="hideMessageActions">
          <div v-if="loadingMessages" class="empty-row">加载消息中...</div>
          <template v-else>
            <div v-for="message in messages" :key="message.id" :class="['message-row', { self: message.senderId === currentUserId }]">
              <img class="avatar" :src="message.senderAvatar || fallbackAvatar" alt="avatar" />
              <div class="bubble-wrap">
                <div class="meta-row">
                  <span>{{ message.senderName || `用户 ${message.senderId}` }}</span>
                  <span>{{ formatTime(message.createdAt) }}</span>
                </div>
                <div class="message-bubble" @click.stop="toggleMessageActions(message)">
                  <template v-if="message.messageType === 'code'">
                    <div class="code-file-card" @click="openCodePreview(message)">
                      <div class="code-file-main">
                        <p class="code-file-name">{{ getCodeFileName(message) }}</p>
                        <p class="code-file-meta">{{ getCodeLanguage(message).toUpperCase() }} · {{ formatCodeSize(message.content) }}</p>
                      </div>
                      <button class="tiny-btn code-open-btn" @click.stop="openCodePreview(message)">查看代码</button>
                    </div>
                  </template>
                  <template v-else>
                    <div v-html="renderMessageHtml(message)"></div>
                  </template>
                </div>
                <div class="message-actions" v-if="message.senderId !== currentUserId && activeMessageActionId === message.id">
                  <button class="tiny-btn danger" @click.stop="reportMessage(message)">举报</button>
                </div>
              </div>
            </div>
            <div v-if="!messages.length" class="empty-row">暂无消息，开始第一句吧</div>
          </template>
        </div>

        <footer class="composer" v-if="activeConversation">
          <div class="composer-top">
            <select v-model="editorType" class="composer-select">
              <option value="text">文本</option>
              <option value="code">代码片段</option>
              <option value="problem_link">题目链接</option>
              <option value="activity_link">活动链接</option>
            </select>
            <select v-if="editorType === 'code'" v-model="codeLanguage" class="composer-select">
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="cpp">C++</option>
              <option value="java">Java</option>
              <option value="go">Go</option>
            </select>
          </div>

          <textarea
            v-if="editorType === 'text'"
            v-model="draftText"
            class="composer-input"
            placeholder="输入消息，支持 @用户名"
            @keyup.ctrl.enter="sendMessage"
          />
          <textarea
            v-else-if="editorType === 'code'"
            v-model="draftCode"
            class="composer-input code"
            placeholder="输入代码，Ctrl + Enter 发送"
            @keyup.ctrl.enter="sendMessage"
          />
          <textarea
            v-else
            v-model="draftLink"
            class="composer-input"
            :placeholder="editorType === 'problem_link' ? '输入题目链接' : '输入活动链接'"
            @keyup.ctrl.enter="sendMessage"
          />

          <div class="composer-bottom">
            <span class="hint">Ctrl + Enter 发送</span>
            <button class="send-btn" :disabled="sending || !canSend" @click="sendMessage">发送</button>
          </div>
        </footer>
      </section>
    </transition>

    <div v-if="groupCreateDialog.visible" class="group-modal-mask" @click.self="closeGroupChatDialog">
      <section class="group-modal">
        <header class="group-modal-header">
          <div>
            <h4>创建群聊</h4>
            <p>选择成员后立即创建群会话</p>
          </div>
          <button class="ghost-btn" @click="closeGroupChatDialog">关闭</button>
        </header>
        <div class="group-modal-body">
          <label class="group-field">
            <span>群聊名称</span>
            <input
              v-model.trim="groupCreateDialog.form.name"
              class="search-input"
              type="text"
              maxlength="32"
              placeholder="输入群聊名称"
            />
          </label>

          <div class="group-field-grid">
            <label class="group-field">
              <span>群类型</span>
              <select v-model="groupCreateDialog.form.conversationType" class="composer-select group-select">
                <option value="custom">自定义</option>
                <option value="class">班级群</option>
                <option value="activity">活动群</option>
                <option value="contest">比赛群</option>
              </select>
            </label>
            <label class="group-field">
              <span>来源ID（可选）</span>
              <input
                v-model.number="groupCreateDialog.form.sourceId"
                class="search-input"
                type="number"
                min="0"
                placeholder="0"
              />
            </label>
          </div>

          <label class="group-field">
            <span>群聊头像</span>
            <div class="group-avatar-row">
              <img
                class="group-avatar-preview"
                :src="groupCreateDialog.form.avatar || fallbackAvatar"
                alt="group-avatar"
              />
              <div class="group-avatar-actions">
                <el-upload
                  class="group-avatar-uploader"
                  :action="groupCreateDialog.qiniuDomain"
                  :data="groupCreateDialog.uploadData"
                  :show-file-list="false"
                  :before-upload="beforeGroupAvatarUpload"
                  :on-success="onGroupAvatarUploadSuccess"
                  :on-error="onGroupAvatarUploadError"
                >
                  <button class="tiny-btn" type="button" :disabled="groupCreateDialog.avatarUploading">
                    {{ groupCreateDialog.avatarUploading ? "上传中..." : "上传头像" }}
                  </button>
                </el-upload>
                <button
                  v-if="groupCreateDialog.form.avatar"
                  class="tiny-btn danger"
                  type="button"
                  @click="clearGroupAvatar"
                >
                  移除
                </button>
              </div>
            </div>
          </label>

          <label class="group-field">
            <span>搜索成员</span>
            <input
              v-model.trim="groupCreateDialog.keyword"
              class="search-input"
              type="text"
              placeholder="输入用户名或ID筛选"
            />
          </label>

          <div class="group-member-list">
            <label
              v-for="friend in groupDialogFilteredFriends"
              :key="friend.id"
              class="group-member-item"
            >
              <input
                v-model="groupCreateDialog.form.memberIds"
                type="checkbox"
                :value="friend.id"
                :disabled="friend.blocked"
              />
              <img class="avatar" :src="friend.avatar || fallbackAvatar" alt="avatar" />
              <span class="group-member-name">{{ friend.username }}</span>
              <span class="group-member-id">#{{ friend.id }}</span>
              <span class="group-member-status" :class="{ online: !!friend.online }">{{ friend.online ? '在线' : '离线' }}</span>
            </label>
            <div v-if="!groupDialogFilteredFriends.length" class="empty-row">暂无可选成员</div>
          </div>

          <p class="group-member-count">已选成员：{{ groupCreateDialog.form.memberIds.length }}（不含自己）</p>
        </div>
        <footer class="group-modal-footer">
          <button class="ghost-btn" @click="closeGroupChatDialog">取消</button>
          <button
            class="send-btn"
            :disabled="groupCreateDialog.submitting || !canSubmitGroupChatDialog"
            @click="submitGroupChatDialog"
          >
            {{ groupCreateDialog.submitting ? '创建中...' : '创建群聊' }}
          </button>
        </footer>
      </section>
    </div>

    <div v-if="codePreview.visible" class="code-modal-mask" @click.self="closeCodePreview">
      <section class="code-modal">
        <header class="code-modal-header">
          <div>
            <h4>{{ codePreview.title }}</h4>
            <p>{{ codePreview.language.toUpperCase() }} · {{ codePreview.lineCount }} 行</p>
          </div>
          <button class="ghost-btn" @click="closeCodePreview">关闭</button>
        </header>
        <div class="code-modal-body">
          <pre><code v-html="highlightCode(codePreview.content, codePreview.language)"></code></pre>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { showLoginRequiredPrompt } from "@/utils/loginRequiredPrompt";

const WS_RECONNECT_DELAY = 2500;
const UNREAD_REFRESH_INTERVAL = 12000;
const FRIEND_FETCH_LIMIT = 5000;

export default {
  name: "GlobalChatDock",
  data() {
    return {
      fallbackAvatar: "https://cdn.codeac.com.cn/logo",
      activeTab: "conversations",
      showFriendPanel: false,
      showChatWindow: false,
      loadingFriends: false,
      loadingConversations: false,
      loadingMessages: false,
      friends: [],
      conversations: [],
      activeConversation: null,
      messages: [],
      activeMessageActionId: null,
      listKeyword: "",
      messageKeyword: "",
      editorType: "text",
      codeLanguage: "javascript",
      draftText: "",
      draftCode: "",
      draftLink: "",
      sending: false,
      ws: null,
      wsConnected: false,
      wsReconnectTimer: null,
      unreadRefreshTimer: null,
      friendSearchTimer: null,
      friendSearchRequestId: 0,
      groupCreateDialog: {
        visible: false,
        submitting: false,
        avatarUploading: false,
        keyword: "",
        qiniuDomain: "https://upload.qiniup.com",
        qiniuAddr: "https://cdn.codeac.com.cn",
        uploadData: {
          key: "",
          token: "",
        },
        form: {
          name: "",
          avatar: "",
          conversationType: "custom",
          sourceId: 0,
          memberIds: [],
        },
      },
      codePreview: {
        visible: false,
        title: "",
        language: "text",
        content: "",
        lineCount: 0,
      },
    };
  },
  computed: {
    isLogin() {
      return !!this.token;
    },
    token() {
      return this.$store?.getters?.token || this.$store?.state?.token || "";
    },
    currentUserId() {
      return Number(this.$store?.getters?.userInfo?.id || this.$store?.state?.userInfo?.id || 0);
    },
    normalizedUserInfo() {
      const raw = this.$store?.getters?.userInfo || this.$store?.state?.userInfo || {};
      if (!raw) return {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (error) {
          return {};
        }
      }
      return raw;
    },
    canCreateGroupChat() {
      const userInfo = this.normalizedUserInfo || {};
      const roleName = String(userInfo.roleName || userInfo.role_name || "").toLowerCase();
      const adminType = String(userInfo.admin_type || userInfo.adminType || "").toLowerCase();
      const compactRoleName = roleName.replace(/[\s_-]/g, "");
      const compactAdminType = adminType.replace(/[\s_-]/g, "");
      const isSuperAdmin = userInfo.isSuperAdmin === true || userInfo.isSuperAdmin === "true";
      const isAdminFlag = Number(userInfo.isAdmin || 0) >= 1;
      return isSuperAdmin ||
        isAdminFlag ||
        compactRoleName === "admin" ||
        compactRoleName === "superadmin" ||
        compactAdminType === "admin" ||
        compactAdminType === "superadmin";
    },
    fabUnreadCount() {
      return this.conversations.reduce((total, item) => total + Number(item.unreadCount || 0), 0);
    },
    shouldBlinkCollapsedHeader() {
      return !this.showFriendPanel && this.fabUnreadCount > 0;
    },
    filteredFriends() {
      const keyword = this.listKeyword.toLowerCase();
      if (!keyword) return this.friends;
      return this.friends.filter((item) => {
        const name = String(item.username || "").toLowerCase();
        const userId = String(item.id || "");
        return name.includes(keyword) || userId.includes(keyword);
      });
    },
    friendSearchKeyword() {
      return String(this.listKeyword || "").trim();
    },
    directConversationPeerIdSet() {
      const set = new Set();
      for (const conversation of this.conversations || []) {
        if (conversation?.conversationType === "direct" && conversation?.peerUser?.id) {
          set.add(Number(conversation.peerUser.id));
        }
      }
      return set;
    },
    visibleFriends() {
      if (!this.friendSearchKeyword) {
        return [];
      }
      return this.filteredFriends;
    },
    filteredConversations() {
      const keyword = this.listKeyword.toLowerCase();
      if (!keyword) return this.conversations;
      return this.conversations.filter((item) => {
        const title = this.getConversationTitle(item).toLowerCase();
        const preview = this.getConversationPreview(item).toLowerCase();
        return title.includes(keyword) || preview.includes(keyword);
      });
    },
    groupDialogCandidateFriends() {
      return (this.friends || []).filter((friend) => !friend.blocked);
    },
    groupDialogFilteredFriends() {
      const keyword = String(this.groupCreateDialog?.keyword || "").trim().toLowerCase();
      if (!keyword) {
        return this.groupDialogCandidateFriends;
      }
      return this.groupDialogCandidateFriends.filter((friend) => {
        const username = String(friend.username || "").toLowerCase();
        const userId = String(friend.id || "");
        return username.includes(keyword) || userId.includes(keyword);
      });
    },
    canSubmitGroupChatDialog() {
      const name = String(this.groupCreateDialog?.form?.name || "").trim();
      const memberCount = (this.groupCreateDialog?.form?.memberIds || []).length;
      return name.length > 0 && memberCount > 0;
    },
    canSend() {
      if (this.editorType === "text") return this.draftText.trim().length > 0;
      if (this.editorType === "code") return this.draftCode.trim().length > 0;
      return this.draftLink.trim().length > 0;
    },
  },
  watch: {
    token(next) {
      if (!next) {
        this.stopBackgroundSync();
        this.resetDock();
        this.disconnectWs();
        return;
      }
      this.initBackgroundSync();
    },
    listKeyword(next) {
      if (!this.showFriendPanel || this.activeTab !== "friends") return;
      this.scheduleFriendSearch(next);
    },
    activeTab(next) {
      if (next === "friends" && this.showFriendPanel) {
        this.scheduleFriendSearch(this.listKeyword);
      }
    },
  },
  mounted() {
    if (this.isLogin) {
      this.initBackgroundSync();
    }
  },
  beforeUnmount() {
    this.stopBackgroundSync();
    this.disconnectWs();
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
    if (this.friendSearchTimer) {
      clearTimeout(this.friendSearchTimer);
      this.friendSearchTimer = null;
    }
  },
  methods: {
    async request(config) {
      const response = await this.axios(config);
      const payload = response?.data || {};
      if (payload.error_code !== 0) {
        throw new Error(payload.message || "请求失败");
      }
      return payload.data;
    },
    async toggleFriendPanel() {
      if (!this.isLogin) {
        showLoginRequiredPrompt(this, "请先登录后使用站内聊天");
        return;
      }
      this.showFriendPanel = !this.showFriendPanel;
      if (this.showFriendPanel) {
        await this.bootstrapChat();
      }
    },
    async bootstrapChat() {
      await this.fetchConversations();
      this.connectWs();
    },
    initBackgroundSync() {
      if (!this.isLogin) return;
      this.connectWs();
      this.fetchConversations({ silent: true, showError: false });
      if (this.unreadRefreshTimer) return;
      this.unreadRefreshTimer = setInterval(() => {
        if (!this.isLogin) return;
        this.fetchConversations({ silent: true, showError: false });
      }, UNREAD_REFRESH_INTERVAL);
    },
    stopBackgroundSync() {
      if (this.unreadRefreshTimer) {
        clearInterval(this.unreadRefreshTimer);
        this.unreadRefreshTimer = null;
      }
    },
    scheduleFriendSearch(keyword) {
      if (this.friendSearchTimer) {
        clearTimeout(this.friendSearchTimer);
        this.friendSearchTimer = null;
      }
      const normalizedKeyword = String(keyword || "").trim();
      if (!normalizedKeyword) {
        this.friendSearchRequestId += 1;
        this.loadingFriends = false;
        this.friends = [];
        return;
      }
      this.friendSearchTimer = setTimeout(() => {
        this.fetchFriends(normalizedKeyword);
      }, 220);
    },
    async fetchFriends(keyword = "", options = {}) {
      const { allowEmptyKeyword = false } = options;
      const normalizedKeyword = String(keyword || "").trim();
      if (!normalizedKeyword && !allowEmptyKeyword) {
        this.friends = [];
        this.loadingFriends = false;
        return;
      }
      const requestId = ++this.friendSearchRequestId;
      this.loadingFriends = true;
      try {
        const data = await this.request({
          method: "get",
          url: "/api/chat/friends",
          params: {
            keyword: normalizedKeyword,
            limit: FRIEND_FETCH_LIMIT,
          },
        });
        if (requestId !== this.friendSearchRequestId) return;
        this.friends = this.uniqueById(data?.friends || [], "id");
      } catch (error) {
        if (requestId === this.friendSearchRequestId) {
          this.handleError(error, "加载好友失败");
        }
      } finally {
        if (requestId === this.friendSearchRequestId) {
          this.loadingFriends = false;
        }
      }
    },
    async fetchConversations(options = {}) {
      const { silent = false, showError = true } = options;
      if (!silent) {
        this.loadingConversations = true;
      }
      try {
        const data = await this.request({
          method: "get",
          url: "/api/chat/conversations",
        });
        this.conversations = this.uniqueById(data?.conversations || [], "id");
      } catch (error) {
        if (showError) {
          this.handleError(error, "加载会话失败");
        }
      } finally {
        if (!silent) {
          this.loadingConversations = false;
        }
      }
    },
    async openDirectConversation(friend) {
      try {
        const data = await this.request({
          method: "post",
          url: "/api/chat/conversations/direct",
          data: {
            targetUserId: friend.id,
          },
        });
        const conversation = data?.conversation;
        if (!conversation) {
          throw new Error("会话创建失败");
        }
        this.upsertConversation(conversation, false);
        await this.openConversation(conversation);
      } catch (error) {
        this.handleError(error, "打开会话失败");
      }
    },
    async openConversation(conversation) {
      this.activeConversation = { ...conversation };
      this.showChatWindow = true;
      this.activeMessageActionId = null;
      await this.fetchMessages();
      this.markCurrentConversationRead();
    },
    closeChatWindow() {
      this.showChatWindow = false;
      this.messages = [];
      this.activeMessageActionId = null;
      this.activeConversation = null;
      this.messageKeyword = "";
    },
    async fetchMessages(search = "") {
      if (!this.activeConversation?.id) return;
      this.loadingMessages = true;
      try {
        const data = await this.request({
          method: "get",
          url: `/api/chat/conversations/${this.activeConversation.id}/messages`,
          params: {
            offset: 0,
            limit: 80,
            search,
          },
        });
        this.messages = data?.messages || [];
        this.activeMessageActionId = null;
        if (data?.conversation) {
          this.activeConversation = { ...data.conversation };
          this.upsertConversation(data.conversation, false);
        }
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        this.handleError(error, "加载消息失败");
      } finally {
        this.loadingMessages = false;
      }
    },
    async searchMessages() {
      await this.fetchMessages(this.messageKeyword);
    },
    async sendMessage() {
      if (!this.activeConversation?.id || !this.canSend || this.sending) {
        return;
      }
      this.sending = true;
      const payload = {
        conversationId: this.activeConversation.id,
        messageType: this.editorType,
        content: "",
        extra: {},
      };
      if (this.editorType === "text") {
        payload.content = this.draftText.trim();
      } else if (this.editorType === "code") {
        payload.content = this.draftCode.trim();
        payload.extra = { language: this.codeLanguage };
      } else {
        payload.content = this.draftLink.trim();
      }

      try {
        const data = await this.request({
          method: "post",
          url: "/api/chat/messages",
          data: payload,
        });
        const message = data?.message;
        if (message) {
          this.pushIncomingMessage(message, false);
        }
        this.clearComposer();
      } catch (error) {
        this.handleError(error, "发送失败");
      } finally {
        this.sending = false;
      }
    },
    clearComposer() {
      this.draftText = "";
      this.draftCode = "";
      this.draftLink = "";
    },
    async markCurrentConversationRead() {
      if (!this.activeConversation?.id) return;
      const lastMessage = this.messages.length ? this.messages[this.messages.length - 1] : null;
      const lastMessageId = lastMessage?.id || this.activeConversation?.lastMessage?.id || 0;
      try {
        await this.request({
          method: "post",
          url: `/api/chat/conversations/${this.activeConversation.id}/read`,
          data: {
            lastMessageId,
          },
        });
        this.conversations = this.conversations.map((item) => {
          if (item.id !== this.activeConversation.id) return item;
          return {
            ...item,
            unreadCount: 0,
            mentionCount: 0,
          };
        });
      } catch (error) {
        this.handleError(error, "设置已读失败");
      }
    },
    async blockUser(friend) {
      const reason = window.prompt("输入屏蔽原因（可选）", "") || "";
      try {
        await this.request({
          method: "post",
          url: `/api/chat/users/${friend.id}/block`,
          data: {
            reason,
          },
        });
        this.friends = this.friends.map((item) => item.id === friend.id ? { ...item, blocked: true } : item);
        ElMessage.success("已屏蔽该用户");
      } catch (error) {
        this.handleError(error, "屏蔽失败");
      }
    },
    async unblockUser(friend) {
      try {
        await this.request({
          method: "delete",
          url: `/api/chat/users/${friend.id}/block`,
        });
        this.friends = this.friends.map((item) => item.id === friend.id ? { ...item, blocked: false } : item);
        ElMessage.success("已取消屏蔽");
      } catch (error) {
        this.handleError(error, "取消屏蔽失败");
      }
    },
    async reportMessage(message) {
      const reason = window.prompt("请输入举报原因", "不友善内容");
      if (!reason) return;
      try {
        await this.request({
          method: "post",
          url: `/api/chat/messages/${message.id}/report`,
          data: {
            reason,
          },
        });
        this.activeMessageActionId = null;
        ElMessage.success("举报已提交");
      } catch (error) {
        this.handleError(error, "举报失败");
      }
    },
    toggleMessageActions(message) {
      if (!message || message.senderId === this.currentUserId) {
        return;
      }
      if (this.activeMessageActionId === message.id) {
        this.activeMessageActionId = null;
        return;
      }
      this.activeMessageActionId = message.id;
    },
    hideMessageActions() {
      this.activeMessageActionId = null;
    },
    async fetchGroupAvatarUploadToken() {
      try {
        const response = await this.axios({
          method: "get",
          url: "/api/getQiniuToken",
        });
        const payload = response?.data || {};
        if (payload.error_code !== 0 || !payload.data) {
          throw new Error(payload.message || "获取上传凭证失败");
        }
        this.groupCreateDialog.uploadData.token = payload.data;
        return true;
      } catch (error) {
        this.handleError(error, "获取头像上传凭证失败");
        return false;
      }
    },
    beforeGroupAvatarUpload(file) {
      if (!this.groupCreateDialog.uploadData.token) {
        ElMessage.error("上传凭证失效，请重试");
        return false;
      }
      const isPNG = file.type === "image/png";
      const isJPEG = file.type === "image/jpeg";
      const isJPG = file.type === "image/jpg";
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isPNG && !isJPEG && !isJPG) {
        ElMessage.error("头像仅支持 JPG / PNG / JPEG");
        return false;
      }
      if (!isLt2M) {
        ElMessage.error("头像大小不能超过 2MB");
        return false;
      }
      const fileName = String(file.name || "").toLowerCase();
      const matched = fileName.match(/\.[a-z0-9]+$/);
      const suffix = matched ? matched[0] : ".jpg";
      const randomPart = Math.random().toString(36).slice(2, 8);
      this.groupCreateDialog.uploadData.key = `chat_group_${this.currentUserId}_${Date.now()}_${randomPart}${suffix}`;
      this.groupCreateDialog.avatarUploading = true;
      return true;
    },
    onGroupAvatarUploadSuccess(response) {
      const key = response?.key;
      if (!key) {
        this.groupCreateDialog.avatarUploading = false;
        ElMessage.error("头像上传返回异常");
        return;
      }
      this.groupCreateDialog.form.avatar = `${this.groupCreateDialog.qiniuAddr}/${key}`;
      this.groupCreateDialog.avatarUploading = false;
      ElMessage.success("群头像上传成功");
    },
    onGroupAvatarUploadError() {
      this.groupCreateDialog.avatarUploading = false;
      ElMessage.error("群头像上传失败");
    },
    clearGroupAvatar() {
      this.groupCreateDialog.form.avatar = "";
    },
    async openGroupChatDialog() {
      if (!this.canCreateGroupChat) {
        ElMessage.warning("仅管理员可拉人进群");
        return;
      }
      if (!this.friends.length) {
        await this.fetchFriends(this.listKeyword, { allowEmptyKeyword: true });
      }
      const candidates = (this.friends || []).filter((friend) => !friend.blocked);
      if (!candidates.length) {
        ElMessage.warning("至少需要一个可聊天成员才能建群");
        return;
      }
      if (!this.groupCreateDialog.uploadData.token) {
        const tokenOk = await this.fetchGroupAvatarUploadToken();
        if (!tokenOk) {
          return;
        }
      }
      this.groupCreateDialog.visible = true;
      this.groupCreateDialog.submitting = false;
      this.groupCreateDialog.avatarUploading = false;
      this.groupCreateDialog.keyword = "";
      this.groupCreateDialog.form = {
        name: "活动讨论群",
        avatar: "",
        conversationType: "custom",
        sourceId: 0,
        memberIds: candidates.slice(0, 3).map((friend) => friend.id),
      };
    },
    closeGroupChatDialog(force = false) {
      if ((this.groupCreateDialog.submitting || this.groupCreateDialog.avatarUploading) && !force) return;
      this.groupCreateDialog.visible = false;
      this.groupCreateDialog.avatarUploading = false;
      this.groupCreateDialog.keyword = "";
      this.groupCreateDialog.form = {
        name: "",
        avatar: "",
        conversationType: "custom",
        sourceId: 0,
        memberIds: [],
      };
    },
    async submitGroupChatDialog() {
      if (!this.canCreateGroupChat) {
        ElMessage.warning("仅管理员可拉人进群");
        return;
      }
      if (!this.canSubmitGroupChatDialog || this.groupCreateDialog.submitting) {
        return;
      }

      const name = String(this.groupCreateDialog.form.name || "").trim();
      const avatar = String(this.groupCreateDialog.form.avatar || "").trim();
      const sourceType = String(this.groupCreateDialog.form.conversationType || "custom").trim().toLowerCase();
      const sourceId = Number(this.groupCreateDialog.form.sourceId || 0);
      const memberIds = Array.from(new Set(
        (this.groupCreateDialog.form.memberIds || [])
          .map((item) => Number(item))
          .filter((id) => id > 0)
      ));

      if (!name) {
        ElMessage.warning("请输入群聊名称");
        return;
      }
      if (!memberIds.length) {
        ElMessage.warning("至少选择一位成员");
        return;
      }

      try {
        this.groupCreateDialog.submitting = true;
        const data = await this.request({
          method: "post",
          url: "/api/chat/conversations/group",
          data: {
            name,
            avatar,
            conversationType: sourceType,
            sourceType,
            sourceId: sourceId > 0 ? sourceId : 0,
            memberIds,
          },
        });
        const conversation = data?.conversation;
        if (conversation) {
          this.upsertConversation(conversation, true);
          ElMessage.success("群聊创建成功");
          this.activeTab = "conversations";
          this.closeGroupChatDialog(true);
        }
      } catch (error) {
        this.handleError(error, "创建群聊失败");
      } finally {
        this.groupCreateDialog.submitting = false;
      }
    },
    connectWs() {
      if (!this.isLogin || this.ws) {
        return;
      }
      try {
        const wsURL = this.buildWsURL();
        this.ws = new WebSocket(wsURL);
      } catch (error) {
        this.ws = null;
        this.wsConnected = false;
        return;
      }

      this.ws.onopen = () => {
        this.wsConnected = true;
      };
      this.ws.onclose = () => {
        this.wsConnected = false;
        this.ws = null;
        this.scheduleWsReconnect();
      };
      this.ws.onerror = () => {
        this.wsConnected = false;
      };
      this.ws.onmessage = (event) => {
        this.handleWsPayload(event.data);
      };
    },
    scheduleWsReconnect() {
      if (!this.isLogin || this.wsReconnectTimer) {
        return;
      }
      this.wsReconnectTimer = setTimeout(() => {
        this.wsReconnectTimer = null;
        this.connectWs();
      }, WS_RECONNECT_DELAY);
    },
    disconnectWs() {
      if (this.wsReconnectTimer) {
        clearTimeout(this.wsReconnectTimer);
        this.wsReconnectTimer = null;
      }
      if (this.ws) {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
        this.ws = null;
      }
      this.wsConnected = false;
    },
    handleWsPayload(raw) {
      let payload = null;
      try {
        payload = JSON.parse(raw);
      } catch (error) {
        return;
      }
      if (!payload || !payload.event) {
        return;
      }
      if (payload.event === "message" && payload.message) {
        this.pushIncomingMessage(payload.message, true);
      }
    },
    pushIncomingMessage(message, fromWS) {
      if (!message?.id || !message?.conversationId) {
        return;
      }
      this.upsertConversationByMessage(message, fromWS);
      if (this.activeConversation?.id === message.conversationId) {
        if (!this.messages.find((item) => item.id === message.id)) {
          this.messages.push(message);
        }
        this.$nextTick(() => this.scrollToBottom());
        this.markCurrentConversationRead();
      }
    },
    upsertConversationByMessage(message, fromWS) {
      const index = this.conversations.findIndex((item) => item.id === message.conversationId);
      if (index === -1) {
        const list = [{
          id: message.conversationId,
          name: `会话 #${message.conversationId}`,
          conversationType: "group",
          sourceType: "custom",
          unreadCount: this.activeConversation?.id === message.conversationId ? 0 : 1,
          mentionCount: 0,
          lastMessage: message,
          updatedAt: message.createdAt,
        }, ...this.conversations];
        this.conversations = this.uniqueById(list, "id");
        return;
      }

      const conversation = { ...this.conversations[index] };
      if (conversation?.lastMessage?.id === message.id) {
        return;
      }

      conversation.lastMessage = message;
      conversation.updatedAt = message.createdAt;
      if (this.activeConversation?.id !== message.conversationId) {
        if (fromWS || message.senderId !== this.currentUserId) {
          conversation.unreadCount = Number(conversation.unreadCount || 0) + 1;
          if (Array.isArray(message.mentionedUserIds) && message.mentionedUserIds.includes(this.currentUserId)) {
            conversation.mentionCount = Number(conversation.mentionCount || 0) + 1;
          }
        }
      } else {
        conversation.unreadCount = 0;
        conversation.mentionCount = 0;
      }

      const updated = [...this.conversations];
      updated.splice(index, 1);
      updated.unshift(conversation);
      this.conversations = this.uniqueById(updated, "id");
    },
    upsertConversation(conversation, toTop) {
      const index = this.conversations.findIndex((item) => item.id === conversation.id);
      if (index === -1) {
        if (toTop) {
          this.conversations = this.uniqueById([conversation, ...this.conversations], "id");
        } else {
          this.conversations = this.uniqueById([...this.conversations, conversation], "id");
        }
        return;
      }
      const copied = [...this.conversations];
      copied[index] = { ...copied[index], ...conversation };
      if (toTop) {
        const [target] = copied.splice(index, 1);
        copied.unshift(target);
      }
      this.conversations = this.uniqueById(copied, "id");
    },
    uniqueById(list, field = "id") {
      const map = new Map();
      for (const item of list || []) {
        const key = item?.[field];
        if (key === null || key === undefined) continue;
        if (!map.has(key)) {
          map.set(key, item);
        }
      }
      return Array.from(map.values());
    },
    getConversationTitle(conversation) {
      if (!conversation) return "";
      return conversation.peerUser?.username || conversation.name || `会话 #${conversation.id}`;
    },
    getConversationAvatar(conversation) {
      if (!conversation) return this.fallbackAvatar;
      return conversation.peerUser?.avatar || conversation.avatar || this.fallbackAvatar;
    },
    getConversationTypeLabel(conversation) {
      const type = conversation?.conversationType;
      if (type === "direct") return "单聊";
      if (type === "class") return "班级群";
      if (type === "activity") return "活动群";
      if (type === "contest") return "比赛群";
      return "群聊";
    },
    isConversationOnline(conversation) {
      return !!conversation?.peerUser?.online;
    },
    getCodeLanguage(message) {
      const extra = this.parseExtra(message?.extra);
      const lang = String(extra?.language || "text").trim().toLowerCase();
      return lang || "text";
    },
    getCodeFileName(message) {
      const lang = this.getCodeLanguage(message);
      const extMap = {
        javascript: "js",
        python: "py",
        cpp: "cpp",
        java: "java",
        go: "go",
        text: "txt",
      };
      const ext = extMap[lang] || "txt";
      return `snippet-${message?.id || "new"}.${ext}`;
    },
    formatCodeSize(content) {
      const size = String(content || "").length;
      if (size < 1024) return `${size} B`;
      return `${(size / 1024).toFixed(1)} KB`;
    },
    openCodePreview(message) {
      const content = String(message?.content || "");
      const language = this.getCodeLanguage(message);
      this.codePreview = {
        visible: true,
        title: this.getCodeFileName(message),
        language,
        content,
        lineCount: content ? content.split(/\r?\n/).length : 0,
      };
    },
    closeCodePreview() {
      this.codePreview.visible = false;
    },
    getConversationPreview(conversation) {
      const message = conversation?.lastMessage;
      if (!message) return "暂无消息";
      if (message.messageType === "code") return "[代码文件]";
      if (message.messageType === "problem_link") return "[题目链接]";
      if (message.messageType === "activity_link") return "[活动链接]";
      return String(message.content || "").slice(0, 24);
    },
    buildWsURL() {
      const base = this.axios?.defaults?.baseURL || window.location.origin;
      const parsed = new URL(base, window.location.origin);
      const protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
      return `${protocol}//${parsed.host}/api/chat/ws?token=${encodeURIComponent(this.token)}`;
    },
    renderMessageHtml(message) {
      const content = String(message?.content || "");
      const extra = this.parseExtra(message?.extra);
      if (message.messageType === "code") {
        const lang = String(extra.language || "text");
        const html = this.highlightCode(content, lang);
        return `<div class="code-lang">${this.escapeHtml(lang)}</div><pre><code>${html}</code></pre>`;
      }
      if (message.messageType === "problem_link") {
        const safe = this.escapeHtml(content);
        return `<a href="${safe}" target="_blank" rel="noopener noreferrer">题目链接：${safe}</a>`;
      }
      if (message.messageType === "activity_link") {
        const safe = this.escapeHtml(content);
        return `<a href="${safe}" target="_blank" rel="noopener noreferrer">活动链接：${safe}</a>`;
      }
      return this.linkifyText(content);
    },
    linkifyText(text) {
      const escaped = this.escapeHtml(text);
      const withMention = escaped.replace(/(^|\s)@([A-Za-z0-9_]{1,32})/g, "$1<span class=\"mention\">@$2</span>");
      return withMention.replace(
        /(https?:\/\/[\w\-./?%&=+#:;~]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      );
    },
    highlightCode(code, language) {
      const escaped = this.escapeHtml(code);
      const keywordSet = {
        javascript: ["const", "let", "var", "function", "return", "if", "else", "for", "while", "class", "new", "await", "async"],
        python: ["def", "return", "if", "elif", "else", "for", "while", "import", "from", "class", "try", "except"],
        java: ["public", "private", "class", "static", "void", "if", "else", "for", "while", "return", "new"],
        cpp: ["int", "long", "double", "string", "if", "else", "for", "while", "return", "class", "include"],
        go: ["func", "package", "import", "if", "else", "for", "range", "return", "struct", "interface", "go", "defer"],
      };
      const keywords = keywordSet[language] || [];
      if (!keywords.length) {
        return escaped;
      }
      const keywordReg = new RegExp(`\\b(${keywords.join("|")})\\b`, "g");
      return escaped
        .replace(keywordReg, '<span class="token keyword">$1</span>')
        .replace(/\b(\d+)\b/g, '<span class="token number">$1</span>');
    },
    parseExtra(extra) {
      if (!extra) return {};
      if (typeof extra === "object") return extra;
      try {
        return JSON.parse(extra);
      } catch (error) {
        return {};
      }
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    formatTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      return `${date.getMonth() + 1}-${date.getDate()} ${hh}:${mm}`;
    },
    formatBadge(count) {
      if (count > 99) return "99+";
      return String(count);
    },
    scrollToBottom() {
      const container = this.$refs.messageListRef;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    resetDock() {
      this.showFriendPanel = false;
      this.showChatWindow = false;
      this.groupCreateDialog.visible = false;
      this.groupCreateDialog.submitting = false;
      this.groupCreateDialog.avatarUploading = false;
      this.groupCreateDialog.keyword = "";
      this.groupCreateDialog.form = {
        name: "",
        avatar: "",
        conversationType: "custom",
        sourceId: 0,
        memberIds: [],
      };
      this.activeConversation = null;
      this.messages = [];
      this.conversations = [];
      this.friends = [];
      this.clearComposer();
    },
    handleError(error, fallbackText) {
      const msg = error?.message || fallbackText || "操作失败";
      ElMessage.error(msg);
    },
  },
};
</script>

<style scoped>
.chat-dock {
  --chat-accent: #1f9d77;
  --chat-accent-dark: #0d6a4f;
  --chat-soft: #f3fbf8;
  --chat-border: #d2e8e0;
  --chat-text: #10322a;
  --chat-muted: #5a7a71;
  --chat-alert: #d94c3f;
  --chat-font: "Space Grotesk", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  --chat-dock-right: 24px;
  --chat-panel-bottom: 0px;
  --chat-panel-width: 340px;
  --chat-panel-height: 540px;
  --chat-panel-collapsed-height: 64px;
  --chat-panel-gap: 14px;
  font-family: var(--chat-font);
}

.fab-badge {
  display: inline-block;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 20px;
  background: #ff5546;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.friend-panel {
  position: fixed;
  right: var(--chat-dock-right);
  bottom: var(--chat-panel-bottom);
  width: var(--chat-panel-width);
  height: var(--chat-panel-collapsed-height);
  background: linear-gradient(180deg, #ffffff, #f4fcf8);
  border: 1px solid var(--chat-border);
  border-radius: 18px 18px 0 0;
  box-shadow: 0 20px 36px rgba(12, 54, 42, 0.2);
  z-index: 1699;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: height 0.24s ease, box-shadow 0.24s ease;
}

.friend-panel.expanded {
  height: var(--chat-panel-height);
}

.friend-panel.blink-unread {
  animation: dock-unread-blink 1.2s ease-in-out infinite;
}

.panel-handle {
  cursor: pointer;
  user-select: none;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 14px 10px;
  background: radial-gradient(circle at top left, #e4fff4, #f5f9ff 70%);
  border-bottom: 1px solid var(--chat-border);
  text-align: left;
}

.friend-panel.blink-unread .fab-badge {
  animation: dock-unread-badge-blink 1s ease-in-out infinite;
}

.panel-title {
  flex: 1;
  text-align: left;
}

.friend-panel:not(.expanded) .panel-header {
  border-bottom: none;
  padding: 12px 14px;
}

.panel-header h3 {
  margin: 0;
  color: var(--chat-text);
  font-size: 18px;
}

.panel-header p {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--chat-muted);
}

.panel-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collapse-icon {
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 10px;
  color: #1f6a58;
  background: #e9f8f2;
  font-size: 12px;
  transition: transform 0.2s ease;
}

.collapse-icon.expanded {
  transform: rotate(180deg);
}

.panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.friend-panel:not(.expanded) .panel-body {
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
}

.tab-row {
  display: flex;
  gap: 8px;
  padding: 10px 14px 8px;
}

.tab-btn {
  flex: 1;
  height: 32px;
  border-radius: 10px;
  border: 1px solid var(--chat-border);
  background: #fff;
  color: var(--chat-muted);
  cursor: pointer;
}

.tab-btn.active {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(120deg, #1f9d77, #20b0be);
}

.search-row {
  padding: 0 14px 10px;
}

.search-input {
  width: 100%;
  height: 34px;
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  padding: 0 12px;
  outline: none;
  font-size: 13px;
  color: var(--chat-text);
}

.list-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding: 0 10px 12px;
}

.list-wrap::-webkit-scrollbar {
  width: 8px;
}

.list-wrap::-webkit-scrollbar-thumb {
  background: #bfd6ce;
  border-radius: 999px;
}

.list-wrap::-webkit-scrollbar-thumb:hover {
  background: #9fbbb1;
}

.list-wrap::-webkit-scrollbar-track {
  background: transparent;
}

.friend-item,
.conversation-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 56px 10px 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
}

.friend-item:hover,
.conversation-item:hover {
  border-color: var(--chat-border);
  background: #f7fcfa;
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #d9eee6;
}

.avatar-wrap {
  position: relative;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
}

.online-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: #b9c8c2;
}

.online-dot.online {
  background: #1ecf83;
  box-shadow: 0 0 0 1px rgba(30, 207, 131, 0.22);
}

.friend-info,
.conversation-main {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.name {
  margin: 0;
  font-size: 14px;
  color: var(--chat-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desc,
.preview {
  margin: 4px 0 0;
  color: var(--chat-muted);
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-side {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.unread-tag,
.mention-tag {
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 20px;
  font-size: 11px;
  color: #fff;
  text-align: center;
  font-weight: 700;
}

.unread-tag {
  background: #ff3b30;
  border: 1px solid #ffffff;
  box-shadow: 0 0 0 1px rgba(255, 59, 48, 0.25);
}

.mention-tag {
  background: #ff8f1f;
}

.empty-row {
  text-align: center;
  color: #79988f;
  font-size: 13px;
  padding: 18px 0;
}

.chat-window {
  position: fixed;
  right: calc(var(--chat-dock-right) + var(--chat-panel-width) + var(--chat-panel-gap));
  bottom: var(--chat-panel-bottom);
  width: 470px;
  max-width: calc(100vw - var(--chat-panel-width) - var(--chat-panel-gap) - 2 * var(--chat-dock-right));
  height: var(--chat-panel-height);
  background: linear-gradient(180deg, #f7fffc, #ffffff 20%, #f8fffd);
  border: 1px solid var(--chat-border);
  border-radius: 18px;
  box-shadow: 0 20px 36px rgba(15, 67, 53, 0.2);
  z-index: 1600;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--chat-border);
  background: linear-gradient(100deg, #dbf8ec, #f0f8ff);
}

.chat-header-main {
  text-align: left;
}

.chat-header h3 {
  margin: 0;
  color: var(--chat-text);
  font-size: 17px;
}

.chat-header p {
  margin: 4px 0 0;
  color: var(--chat-muted);
  font-size: 12px;
}

.ws-state {
  margin-left: 8px;
  color: #a26b19;
}

.ws-state.online {
  color: #14865f;
}

.chat-toolbar {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #ecf4f1;
}

.chat-toolbar .search-input {
  flex: 1;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
}

.message-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  align-items: flex-start;
}

.message-row.self {
  flex-direction: row-reverse;
}

.bubble-wrap {
  max-width: calc(100% - 50px);
}

.meta-row {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #6f8a82;
  margin-bottom: 4px;
  justify-content: flex-start;
  text-align: left;
}

.message-row.self .meta-row {
  justify-content: flex-start;
}

.message-bubble {
  background: #fff;
  border: 1px solid #dcf0e8;
  border-radius: 12px;
  padding: 10px 12px;
  color: #1f3c34;
  line-height: 1.55;
  word-break: break-word;
  text-align: left;
}

.message-row.self .message-bubble {
  background: #e6fff5;
  border-color: #bdeed7;
}

.message-bubble pre {
  margin: 8px 0 0;
  border-radius: 10px;
  padding: 10px;
  overflow-x: auto;
  background: #0f1e2e;
  text-align: left;
}

.code-file-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px;
  border: 1px solid #d8e9ff;
  border-radius: 10px;
  background: linear-gradient(120deg, #eef5ff, #f8fbff);
  cursor: pointer;
}

.code-file-main {
  min-width: 0;
  text-align: left;
}

.code-file-name,
.code-file-meta {
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.code-file-name {
  font-size: 13px;
  font-weight: 700;
  color: #1f3c34;
}

.code-file-meta {
  margin-top: 2px;
  font-size: 12px;
  color: #6b8496;
}

.code-open-btn {
  flex: 0 0 auto;
}

.message-bubble code {
  color: #d6f0ff;
  font-size: 12px;
  font-family: "JetBrains Mono", "Cascadia Code", "Fira Code", monospace;
  text-align: left;
}

.code-lang {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 9px;
  font-size: 11px;
  color: #edf8ff;
  background: #1b4f80;
}

.message-bubble .mention {
  color: #1555d5;
  font-weight: 700;
}

.message-bubble a {
  color: #1966d2;
  text-decoration: underline;
}

.token.keyword {
  color: #ffd178;
}

.token.number {
  color: #6ce5c3;
}

.message-actions {
  margin-top: 5px;
  display: flex;
  justify-content: flex-end;
}

.composer {
  border-top: 1px solid #e0f0ea;
  background: #f9fffd;
  padding: 10px 14px 12px;
}

.composer-top {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.composer-select {
  height: 32px;
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  padding: 0 10px;
  color: var(--chat-text);
  background: #fff;
}

.composer-input {
  width: 100%;
  height: 92px;
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  padding: 10px;
  resize: none;
  outline: none;
  font-size: 13px;
  color: var(--chat-text);
}

.composer-input.code {
  font-family: "JetBrains Mono", "Cascadia Code", "Fira Code", monospace;
  background: #13263a;
  color: #f2fbff;
}

.composer-bottom {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hint {
  color: var(--chat-muted);
  font-size: 12px;
}

.send-btn {
  width: 84px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(120deg, #1f9d77, #0d6a4f);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.send-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ghost-btn,
.tiny-btn {
  border: 1px solid #c7e2d8;
  border-radius: 9px;
  background: #fff;
  color: #2d6758;
  cursor: pointer;
}

.ghost-btn {
  height: 30px;
  padding: 0 10px;
}

.tiny-btn {
  height: 26px;
  padding: 0 8px;
  font-size: 12px;
}

.tiny-btn.danger {
  border-color: #f5c5c0;
  color: #cb4f42;
}

.group-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1760;
  background: rgba(8, 21, 17, 0.36);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.group-modal {
  width: min(560px, 94vw);
  max-height: 84vh;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #d8e7e0;
  box-shadow: 0 24px 54px rgba(11, 31, 25, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.group-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e8f2ee;
  background: linear-gradient(120deg, #eef8ff, #f7fffc);
  text-align: left;
}

.group-modal-header h4 {
  margin: 0;
  font-size: 15px;
  color: #1a3a31;
}

.group-modal-header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6a857c;
}

.group-modal-body {
  padding: 12px 14px;
  overflow: auto;
}

.group-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  text-align: left;
}

.group-field > span {
  font-size: 12px;
  color: #5c7b72;
}

.group-field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.group-select {
  width: 100%;
}

.group-avatar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.group-avatar-preview {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #d7e8e1;
  background: #f2f8f5;
}

.group-avatar-actions {
  display: flex;
  gap: 8px;
}

.group-member-list {
  max-height: 230px;
  overflow: auto;
  border: 1px solid #d7e8e1;
  border-radius: 10px;
  padding: 6px;
  background: #fbfffd;
}

.group-member-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
}

.group-member-item:hover {
  background: #f2faf7;
}

.group-member-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #1b3f35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.group-member-id {
  font-size: 12px;
  color: #6f8a82;
}

.group-member-status {
  font-size: 12px;
  color: #8c9994;
}

.group-member-status.online {
  color: #1c9a5f;
}

.group-member-count {
  margin: 10px 0 0;
  font-size: 12px;
  color: #59746c;
  text-align: left;
}

.group-modal-footer {
  padding: 10px 14px 14px;
  border-top: 1px solid #eaf2ee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.code-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1800;
  background: rgba(6, 20, 30, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.code-modal {
  width: min(900px, 92vw);
  max-height: 80vh;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #d8e7e0;
  box-shadow: 0 28px 56px rgba(11, 31, 25, 0.32);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.code-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e8f2ee;
  background: linear-gradient(120deg, #eef8ff, #f7fffc);
  text-align: left;
}

.code-modal-header h4 {
  margin: 0;
  font-size: 15px;
  color: #1a3a31;
}

.code-modal-header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6a857c;
}

.code-modal-body {
  overflow: auto;
  padding: 12px 14px 14px;
  background: #0f1e2e;
  text-align: left;
}

.code-modal-body pre {
  margin: 0;
  text-align: left;
}

.code-modal-body code {
  display: block;
  color: #d6f0ff;
  font-size: 13px;
  line-height: 1.65;
  font-family: "JetBrains Mono", "Cascadia Code", "Fira Code", monospace;
  text-align: left;
}

.dock-pop-enter-active,
.dock-pop-leave-active {
  transition: all 0.22s ease;
}

.dock-pop-enter-from,
.dock-pop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.2s ease;
}

.chat-slide-enter-from,
.chat-slide-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

@keyframes dock-unread-blink {
  0%,
  100% {
    box-shadow: 0 20px 36px rgba(12, 54, 42, 0.2);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.36), 0 20px 38px rgba(12, 54, 42, 0.26);
  }
}

@keyframes dock-unread-badge-blink {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.45;
    transform: scale(0.92);
  }
}

@media (max-width: 960px) {
  .chat-window {
    left: 12px;
    right: 12px;
    width: auto;
    top: 76px;
    bottom: auto;
    height: calc(100vh - 96px);
    max-width: none;
  }

  .friend-panel {
    right: 0;
    left: 0;
    width: auto;
    height: var(--chat-panel-collapsed-height);
    bottom: 0;
  }

  .friend-panel.expanded {
    height: 62vh;
  }

  .group-field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
